import type { AbilityName, AbilityScores, CharacterClass } from '@character-creator/types';

/**
 * Check if ability scores meet multiclass prerequisites for a class.
 * Per 5e rules, you must meet the prerequisites of both your current class
 * and the class you're multiclassing into.
 *
 * Some classes use OR logic (e.g., Fighter needs STR 13 OR DEX 13).
 * Most use AND logic (all requirements must be met).
 */
export function meetsMulticlassPrerequisites(
  classData: Pick<CharacterClass, 'multiclassRequirements' | 'multiclassRequirementsLogic'>,
  abilityScores: AbilityScores,
): boolean {
  const reqs = classData.multiclassRequirements;
  if (!reqs || reqs.length === 0) return true;

  const logic = classData.multiclassRequirementsLogic ?? 'and';

  const check = (req: { ability: AbilityName; minimum: number }) =>
    abilityScores[req.ability] >= req.minimum;

  return logic === 'or' ? reqs.some(check) : reqs.every(check);
}
